import AppleButton from "components/AuthButtons/AppleButton";
import EmailButton from "components/AuthButtons/EmailButton";
import GoogleButton from "components/AuthButtons/GoogleButton";
import LinkedinButton from "components/AuthButtons/LinkedInButton";
import { routes } from "pages/routes";
import { Trans, useTranslation } from "react-i18next";
import { Location, useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { OAuthStep } from "services/oauth";

import { SignUpLocationState } from "../..//SelfSignUp";
import styles from "./SelectMethod.module.scss";
import { trackEvent } from "services/productAnalytics";
import { PurchaseStudentType } from "services/pricingModel/model";

type Props = {
  onEmailSelected: () => void;
  studentType: PurchaseStudentType;
  pricingModelCode?: string;
};

export enum SignUpMethod {
  Google = "google",
  Linkedin = "linkedin",
  Apple = "apple",
  Email = "email",
}

const createMethods = [
  {
    label: "Signup.signupWithEmail",
    method: SignUpMethod.Email,
    component: EmailButton,
  },
  {
    label: "Signup.signupWithGoogle",
    method: SignUpMethod.Google,
    component: GoogleButton,
  },
  {
    label: "Signup.signupWithLinkedIn",
    method: SignUpMethod.Linkedin,
    component: LinkedinButton,
  },
  {
    label: "Signup.signupWithApple",
    method: SignUpMethod.Apple,
    component: AppleButton,
  },
];

export default function SelectMethod({
  onEmailSelected,
  studentType,
  pricingModelCode,
}: Props) {
  const location: Location<SignUpLocationState> = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSelectMethod = (method: SignUpMethod) => {
    if (method === "email") {
      onEmailSelected();
    } else {
      const path = routes.public.signInOAuth.path(
        { provider: method, step: OAuthStep.Start },
        { query: { code: pricingModelCode, studentType } }
      );
      navigate(path, {
        state: { returnPath: location.state?.returnPath },
      });
    }

    trackEvent("sign_up_method_selected", { method });
  };

  return (
    <>
      <div>
        <h2 className={styles.title}>
          {t(`Signup.CreateAccount.headline.${studentType}`)}
        </h2>
        <h3 className={styles.subtitle}>
          {t(`Signup.CreateAccount.subheadline.${studentType}`)}
        </h3>
      </div>

      <div className={styles.methods}>
        {createMethods.map(method => (
          <method.component
            key={method.method}
            onClick={() => handleSelectMethod(method.method)}
          >
            {t(method.label)}
          </method.component>
        ))}
      </div>

      <div className={styles.signIn}>
        <Trans
          i18nKey="Signup.CreateAccount.existingAccount"
          defaults="Already have an account? <0>Sign in here</0>"
          components={[
            <Link
              key="0"
              className={styles.signInLink}
              to={routes.public.signIn.path()}
            />,
          ]}
        />
      </div>
    </>
  );
}
